import { FC, useState, ChangeEvent } from "react";
import { cx } from "@jugl-web/utils";
import { ReactComponent as MinusIcon } from "./assets/minus.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";

export interface InputNumberWithButtonsProps {
  value?: number | null;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (value: number) => string;
  className?: string;
  disabled?: boolean;
  onOutOfRange?: (params: { value: number; min: number; max: number }) => void;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const InputNumberWithButtons: FC<InputNumberWithButtonsProps> = ({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  formatValue = (v) => v.toLocaleString(),
  className,
  disabled = false,
  onOutOfRange,
}) => {
  const [inputDraft, setInputDraft] = useState<string | null>(null);
  const hasValue = value != null;
  const formattedValue = inputDraft ?? (hasValue ? formatValue(value) : "");
  const isMinDisabled = !hasValue || value <= min;
  const isMaxDisabled = hasValue && value >= max;

  const handleDecrement = () => {
    if (!hasValue) return;

    setInputDraft(null);
    onChange(Math.max(min, value - step));
  };

  const handleIncrement = () => {
    setInputDraft(null);
    onChange(hasValue ? Math.min(max, value + step) : min);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    setInputDraft(digits);
    if (digits === "") return;
    const parsed = clamp(Number(digits), min, max);
    if (!hasValue || parsed !== value) onChange(parsed);
  };

  const handleInputBlur = () => {
    if (inputDraft !== null && inputDraft !== "") {
      const parsed = Number(inputDraft);

      if (!Number.isNaN(parsed) && (parsed < min || parsed > max)) {
        onOutOfRange?.({ value: parsed, min, max });
        onChange(clamp(parsed, min, max));
      }
    } else if (inputDraft === "" && hasValue) {
      onChange(min);
    }

    setInputDraft(null);
  };

  return (
    <div
      className={cx(
        "inline-flex w-fit items-center rounded-lg border border-solid border-dark-200 bg-white",
        disabled && "pointer-events-none opacity-40",
        className
      )}
    >
      <button
        type="button"
        className={cx(
          "flex h-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-2",
          isMinDisabled && "cursor-not-allowed opacity-40"
        )}
        onClick={handleDecrement}
        disabled={disabled || isMinDisabled}
      >
        <MinusIcon />
      </button>
      <label className="relative inline-grid items-center px-1">
        <span
          aria-hidden="true"
          className="pointer-events-none invisible col-start-1 row-start-1 px-px text-center font-secondary text-lg leading-normal whitespace-pre tabular-nums"
        >
          {formattedValue || "0"}
        </span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          size={1}
          value={formattedValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          className="col-start-1 row-start-1 m-0 w-full min-w-0 border-none bg-transparent p-0 px-px text-center font-secondary text-lg leading-normal text-dark tabular-nums focus:outline-none"
        />
      </label>

      <button
        type="button"
        className={cx(
          "flex h-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-2",
          isMaxDisabled && "cursor-not-allowed opacity-40"
        )}
        onClick={handleIncrement}
        disabled={disabled || isMaxDisabled}
      >
        <PlusIcon />
      </button>
    </div>
  );
};
